import React, { useState } from 'react';
import ModalPopup from '@ajat/design-system/ModalPopup';
import Button from '@ajat/design-system/Button';
import Input from '@ajat/design-system/Input';
import { ComponentPage, DemoCard } from '../../shared';

const BANNER = require('../../../../../assets/cls-dk/common/school-selector-banner.jpg');

const Stage = ({ mobile = false, children }) => (
  <div
    className={`${mobile ? 'ds-modal-popup-mobile-preview' : 'ds-modal-popup-preview flex items-center justify-center'} bg-[var(--color-gray-025)] p-[var(--space-ui-large-s)] rounded-[var(--radius-card)] border border-[var(--color-gray-1)]`}
  >
    {children}
  </div>
);

export const meta = {
  component: ModalPopup,
  title: 'Modal Popup',
  group: 'Overlays',
  importPath: '@ajat/design-system/ModalPopup',
  description:
    'The bare modal shell: centred card on desktop, bottom sheet with swipe-to-dismiss on mobile, optional banner image, close button and scroll lock — and nothing else. BookMeeting composes it; SchoolSelector and EditOrderLogin still carry their own copies of this chrome and are candidates to migrate onto it.',
  props: [
    ['isOpen', 'bool', 'false', 'Open state'],
    ['onClose', 'func', '—', 'Fires on the X, the overlay, Escape and swipe-down'],
    ['banner', 'node', 'null', 'Banner image or node above the body'],
    ['ariaLabel', 'string', "''", 'Accessible name for the dialog'],
    ['closeAriaLabel', 'string', "'Close'", 'Accessible name for the close button'],
    ['preview', 'bool', 'false', 'Static mode — no overlay, no portal, no scroll lock'],
    ['lockScroll', 'bool', 'true', 'Lock body scroll while open'],
    ['className', 'string', "''", 'Additional CSS class'],
    ['children', 'node', '—', 'Modal body — entirely yours'],
  ],
  propsNote:
    'Do NOT put overflow-y: auto on .ds-modal-popup__content — it kills the flex automatic minimum size and collapses the mobile bottom sheet to zero height.',
  usage: [
    {
      title: 'Building a modal on it',
      code: `import ModalPopup from '@ajat/design-system/ModalPopup';

// BookMeeting is exactly this: the shell plus a form.
<ModalPopup
  isOpen={isOpen}
  onClose={onClose}
  banner={banner}
  ariaLabel={title}
>
  <h2 className="typo-h2">{title}</h2>
  <p className="typo-body">{infoText}</p>
  <Input label="Navn" value={name} onChange={onNameChange} />
  <Button variant="main" onClick={submit}>Fortsæt</Button>
</ModalPopup>`,
    },
  ],
};

const Body = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-s)' }}>
    <h2 className="typo-h2" style={{ color: 'var(--color-gray-10)' }}>Modal title</h2>
    <p className="typo-body" style={{ color: 'var(--color-gray-7)' }}>
      The shell gives you the card, the banner slot, the close button and the scroll lock. Everything
      inside is yours.
    </p>
    <Input label="Navn" />
    <Button variant="main">Fortsæt</Button>
  </div>
);

export default function ModalPopupPage() {
  const [open, setOpen] = useState(false);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Live">
        <Button size="primary" variant="main" onClick={() => setOpen(true)}>Open Modal Popup</Button>
        <ModalPopup isOpen={open} onClose={() => setOpen(false)} banner={BANNER} ariaLabel="Modal title">
          <Body />
        </ModalPopup>
      </DemoCard>

      <DemoCard title="Desktop — with a banner" surface={false}>
        <Stage>
          <ModalPopup isOpen preview onClose={() => {}} banner={BANNER} ariaLabel="Modal title">
            <Body />
          </ModalPopup>
        </Stage>
      </DemoCard>

      <DemoCard title="Desktop — without a banner" surface={false}>
        <Stage>
          <ModalPopup isOpen preview onClose={() => {}} ariaLabel="Modal title">
            <Body />
          </ModalPopup>
        </Stage>
      </DemoCard>

      <DemoCard title="Mobile — bottom sheet" surface={false}>
        <Stage mobile>
          <ModalPopup isOpen preview onClose={() => {}} banner={BANNER} ariaLabel="Modal title">
            <Body />
          </ModalPopup>
        </Stage>
      </DemoCard>
    </ComponentPage>
  );
}
